import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Input, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { MessageBox, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { useUserStore } from "@/views/stores";
import { dateTimeFormat } from "@zhihaoscm/utils";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";

export interface HelpCenterRow {
    id: number;
    title?: string;
    content?: string;
    state?: number;
    updatedTime?: string;
}

export interface HelpCenterPageResult {
    list?: HelpCenterRow[];
    total?: number;
}

export interface HelpCenterQuery {
    page: number;
    size: number;
    title?: string;
    state?: number;
    sortKey?: string;
    sortOrder?: string;
}

export interface HelpCenterPageProps {
    loadHelp?: (query: HelpCenterQuery) => Promise<HelpCenterPageResult>;
    updateStatus?: (id: number, state: number) => Promise<unknown>;
    deleteHelp?: (id: number, payload: { unActiveFileIds: number[] }) => Promise<unknown>;
    permissions?: string[];
}

// 与旧 Vue 接口契约保持默认每页二十条，避免首屏请求和行为用例的分页基线发生变化。
const defaultQuery: HelpCenterQuery = { page: 1, size: 20 };
const stateLabels: Record<number, string> = { 1: "上架", 0: "下架" };

/** 复用 Vue 帮助中心分页接口。 */
export async function loadHelpCenter(query: HelpCenterQuery): Promise<HelpCenterPageResult> {
    const response = await request.service.get<HelpCenterPageResult>("/dataService/admin/help-center/paging", {
        params: query
    });
    return response.data;
}

/** 复用 Vue 帮助中心上架/下架接口。 */
export async function updateHelpCenterStatus(id: number, state: number): Promise<unknown> {
    const response = await request.service.post(`/dataService/admin/help-center/update/${id}/${state}`);
    return response.data;
}

/** 复用 Vue 删除接口，并传递富文本未使用文件清理参数。 */
export async function deleteHelpCenter(id: number, payload: { unActiveFileIds: number[] }): Promise<unknown> {
    const response = await request.service.delete(`/dataService/admin/help-center/${id}`, { data: payload });
    return response.data;
}

/** 从 Vue 富文本内容标记中提取待清理文件 ID。 */
function getUnActiveFileIds(content?: string): number[] {
    const marker = content?.split("\n")[0]?.match(/ids=([^\n]*)/);
    return (
        marker?.[1]
            ?.split("--")
            .flatMap(value => value.split(","))
            .map(value => value.trim())
            .filter(Boolean)
            .map(Number)
            .filter(value => Number.isFinite(value)) || []
    );
}

/** 帮助中心列表，迁移 Vue 列表字段、筛选、排序和状态操作。 */
export function HelpCenterPage({
    loadHelp = loadHelpCenter,
    updateStatus = updateHelpCenterStatus,
    deleteHelp = deleteHelpCenter,
    permissions
}: HelpCenterPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const canWrite = (permissions || storePermissions).includes("ROLE_SS_HELP_W");
    const [rows, setRows] = useState<HelpCenterRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<HelpCenterQuery>(defaultQuery);
    const [title, setTitle] = useState("");
    const [state, setState] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const refresh = async (nextQuery = query) => {
        const result = await loadHelp(nextQuery);
        setRows((result.list || []).map(row => ({ ...row, state: row.state == null ? undefined : Number(row.state) })));
        setTotal(result.total || 0);
    };

    useEffect(() => {
        let disposed = false;
        void loadHelp(query)
            .then(result => {
                if (disposed) return;
                setRows(
                    (result.list || []).map(row => ({
                        ...row,
                        state: row.state == null ? undefined : Number(row.state)
                    }))
                );
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("帮助中心加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadHelp, query]);

    const search = () => {
        setQuery({
            ...defaultQuery,
            ...(title.trim() ? { title: title.trim() } : {}),
            ...(state === "" ? {} : { state: Number(state) })
        });
    };

    const changeStatus = async (row: HelpCenterRow) => {
        try {
            await updateStatus(row.id, row.state === 1 ? 0 : 1);
            notifyOperationSuccess(row.state === 1 ? "已下架" : "已上架");
            await refresh();
            setErrorMessage(null);
        } catch {
            setErrorMessage("帮助中心状态更新失败");
        }
    };

    const removeHelp = async (row: HelpCenterRow) => {
        try {
            await deleteHelp(row.id, { unActiveFileIds: getUnActiveFileIds(row.content) });
            notifyOperationSuccess("已删除");
            await refresh();
            setErrorMessage(null);
        } catch {
            setErrorMessage("帮助中心删除失败");
        }
    };

    // 旧 Vue helpCenterService 的 confirmable 装饰器使用居中的 ZhMessageBox，保持相同文案和交互。
    const confirmStatusChange = async (row: HelpCenterRow) => {
        const message = row.state === 1 ? "是否下架" : "是否上架";
        try {
            await MessageBox.confirm(message, "提示", {
                cancelButtonText: "取消",
                className: "help-center-confirm-message-box",
                closeOnClickModal: false,
                confirmButtonText: "确认",
                showClose: true
            });
        } catch {
            return;
        }
        await changeStatus(row);
    };

    // 旧 Vue 删除接口同样由 confirmable 装饰器包裹，确认前不能发起删除请求。
    const confirmRemoveHelp = async (row: HelpCenterRow) => {
        try {
            await MessageBox.confirm("是否删除", "提示", {
                cancelButtonText: "取消",
                className: "help-center-confirm-message-box",
                closeOnClickModal: false,
                confirmButtonText: "确认",
                showClose: true
            });
        } catch {
            return;
        }
        await removeHelp(row);
    };

    // 保留旧 Vue ProTable 的更新时间排序参数，zhihao-ui 的方向映射为旧接口 sortKey/sortOrder。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "updatedTime") return;
            setQuery(current => {
                const nextQuery = { ...current, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = "updated_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    // 用 zhihao-ui-react 列契约承接旧 Vue 帮助中心 ProTable 的字段和操作插槽。
    const columns: TableColumnProps<HelpCenterRow>[] = [
        {
            key: "title",
            label: "标题",
            prop: "title",
            // 旧 Vue 表格将剩余宽度交给标题列，固定列按 180/114/178px 收敛。
            width: 744,
            render: ({ row }) => (
                <Button
                    type="link"
                    onClick={() => void navigate(`/userOperation/lianYunAbout/helpCenter/info?id=${row.id}`)}
                >
                    {row.title || "-"}
                </Button>
            )
        },
        {
            key: "updatedTime",
            label: "更新时间",
            width: 188,
            prop: "updatedTime",
            sortable: true,
            render: ({ row }) => dateTimeFormat(row.updatedTime) || "-"
        },
        {
            key: "state",
            label: "状态",
            width: 114,
            prop: "state",
            render: ({ row }) => stateLabels[Number(row.state)] || "-"
        },
        {
            key: "operation",
            label: "操作",
            // compact 防止动态按钮测量覆盖旧 Vue 的固定操作列宽度。
            className: "compact",
            width: 178,
            prop: "operation",
            fixed: "right",
            render: ({ row }) => {
                if (!canWrite) return null;
                return row.state === 1 ? (
                    <Button
                        aria-label="下架帮助"
                        disabled={banButton}
                        type="link"
                        onClick={() => void confirmStatusChange(row)}
                    >
                        下架
                    </Button>
                ) : (
                    <Space>
                        <Button
                            aria-label="上架帮助"
                            disabled={banButton}
                            type="link"
                            onClick={() => void confirmStatusChange(row)}
                        >
                            上架
                        </Button>
                        <Button
                            aria-label="编辑帮助"
                            disabled={banButton}
                            type="link"
                            onClick={() => void navigate(`/userOperation/lianYunAbout/helpCenter/edit?id=${row.id}`)}
                        >
                            编辑
                        </Button>
                        <Button
                            aria-label="删除帮助"
                            disabled={banButton}
                            type="link"
                            onClick={() => void confirmRemoveHelp(row)}
                        >
                            删除
                        </Button>
                    </Space>
                );
            }
        }
    ];

    return (
        <section aria-label="help-center-page">
            <Card>
                <ZhTable<HelpCenterRow>
                    data={rows}
                    columns={normalizeOperationColumns(columns, rows)}
                    pagination={false}
                    rowKey={row => String(row.id)}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                帮助中心
                            </Typography.Title>

                            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}

                            <div className="admin-table-filter-form mb-4">
                                {canWrite ? (
                                    <Button
                                        aria-label="新增帮助"
                                        className="admin-table-primary-action"
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() => void navigate("/userOperation/lianYunAbout/helpCenter/add")}
                                    >
                                        新增
                                    </Button>
                                ) : null}
                                <Input
                                    aria-label="帮助中心标题"
                                    placeholder="请输入标题关键词"
                                    value={title}
                                    onChange={event => setTitle(event.target.value)}
                                />
                                <div
                                    onChange={event => {
                                        // 兼容测试和键盘输入直接修改 Ant Select 内部搜索框的事件。
                                        const value = (event.target as HTMLInputElement).value;
                                        if (value in stateLabels) setState(value);
                                    }}
                                >
                                    <Select
                                        allowClear
                                        aria-label="帮助中心状态"
                                        options={Object.entries(stateLabels).map(([value, label]) => ({
                                            label,
                                            value
                                        }))}
                                        placeholder="请选择状态"
                                        value={state || undefined}
                                        onChange={value => setState(value || "")}
                                    />
                                </div>
                                <Button aria-label="查询帮助" type="primary" onClick={search}>
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置"
                                    className="table-filter-reset-button"
                                    onClick={() => {
                                        setTitle("");
                                        setState("");
                                        setQuery(defaultQuery);
                                    }}
                                    icon={<TableSearchIcon name="reset" />}
                                />
                            </div>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                hideOnSinglePage
                                // 沿用 Vue ProTable：切换每页条数后回到第一页，翻页则保留当前条数。
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default HelpCenterPage;
